import { api, throwApiError } from "../../api/api";
import type { AccessModule } from "../../utils/accessModules";

export type UserType =
  | "ADMIN"
  | "SUPERVISOR"
  | "SUPERVISOR_DE_CAMPO"
  | "DP"
  | "FINANCEIRO"
  | "FINANCEIRO_LEITURA"
  /** Tudo o que o ADMIN tem, menos os módulos retirados (ADR 0027). */
  | "ADMIN_RESTRITO"
  /** Só o módulo Campanhas (criador de campanhas, 31/10/2026). */
  | "GESTOR_CAMPANHAS";

/** Únicos papéis que podem ser acesso adicional (ADR 0024); ADMIN não recebe adicional. */
export const MODULE_ROLES: UserType[] = ["DP", "FINANCEIRO", "FINANCEIRO_LEITURA", "GESTOR_CAMPANHAS"];

/** Os dois tipos de administrador (ADR 0027). */
export const isAdminType = (userType: string) => userType === "ADMIN" || userType === "ADMIN_RESTRITO";

export type LoginRequest = {
  identifier: string;
  password: string;
};

export type LoginResponse = {
  tokenType: "Bearer";
  accessToken: string;
  expiresIn: string;
  isFirstLogin: boolean;
  userType: UserType;
  /** Papéis efetivos: principal + adicionais (ADR 0024). */
  roles?: UserType[];
};

export type UserPublic = {
  id: string;
  nomeCompleto: string;
  cpf: string;
  email: string;
  userType: UserType;
  extraRoles?: UserType[];
  revokedModules?: AccessModule[];
  createdAt: string;
  updatedAt?: string;
};

export type CreateUserRequest = {
  nomeCompleto: string;
  cpf: string;
  email?: string;
  userType: UserType;
  /** Acessos adicionais ao tipo principal (ADR 0024). */
  extraRoles?: UserType[];
  /** Direitos retirados do administrador restrito (ADR 0027). */
  revokedModules?: AccessModule[];
  password: string;
  passwordConfirm: string;
  storeIds?: string[];
  /** Celular dos avisos no WhatsApp: só dígitos, com o 55. */
  phone?: string | null;
  /** Desligado, o usuário só recebe os avisos no sino. */
  whatsappOptIn?: boolean;
};

export type UpdateUserRequest = {
  nomeCompleto?: string;
  cpf?: string;
  email?: string;
  userType?: UserType;
  /** Acessos adicionais ao tipo principal (ADR 0024). */
  extraRoles?: UserType[];
  /** Direitos retirados do administrador restrito (só ADMIN altera, ADR 0027). */
  revokedModules?: AccessModule[];
  password?: string;
  passwordConfirm?: string;
  storeIds?: string[];
  /** `null` apaga o celular. */
  phone?: string | null;
  whatsappOptIn?: boolean;
};

export type ChangeFirstLoginPasswordRequest = {
  password: string;
  passwordConfirmation: string;
};

export const loginApi = async (dto: LoginRequest): Promise<LoginResponse> => {
  try {
    const { data } = await api.post<LoginResponse>("/auth/login", dto);
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao realizar login.");
    throw error;
  }
};

export const createUserApi = async (dto: CreateUserRequest): Promise<UserPublic> => {
  try {
    const { data } = await api.post<UserPublic>("/auth/users", dto);
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao criar usuário.");
    throw error;
  }
};

export const updateUserByIdApi = async (
  id: string,
  dto: UpdateUserRequest
): Promise<UserPublic> => {
  try {
    const { data } = await api.patch<UserPublic>(`/auth/users/id/${id}`, dto);
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao atualizar usuário (por ID).");
    throw error;
  }
};

export const deactivateUserByIdApi = async (
  id: string
): Promise<UserPublic> => {
  try {
    const { data } = await api.patch<UserPublic>(`/auth/users/id/${id}/deactivate`, {});
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao desativar usuário.");
    throw error;
  }
};

export const reactivateUserByIdApi = async (
  id: string
): Promise<UserPublic> => {
  try {
    const { data } = await api.patch<UserPublic>(`/auth/users/id/${id}/reactivate`, {});
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao reativar usuário.");
    throw error;
  }
};

export const updateUserByCpfApi = async (
  cpf: string,
  dto: UpdateUserRequest
): Promise<UserPublic> => {
  try {
    const { data } = await api.patch<UserPublic>(`/auth/users/cpf/${cpf}`, dto);
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao atualizar usuário (por CPF).");
    throw error;
  }
};

export const changeFirstLoginPasswordApi = async (
  accessToken: string,
  dto: ChangeFirstLoginPasswordRequest
): Promise<void> => {
  try {
    await api.post("/auth/first-login/change-password", dto, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  } catch (error) {
    throwApiError(error, "Erro ao definir senha no primeiro login.");
    throw error;
  }
};

/** Identificação do próprio usuário do staff — o JWT não carrega o nome. */
export type MeResponse = {
  id: string;
  nomeCompleto: string;
  email: string | null;
  userType: UserType;
  extraRoles?: UserType[];
  /** Papéis efetivos: principal + adicionais (ADR 0024). */
  roles?: UserType[];
};

export const getMeApi = async (accessToken: string): Promise<MeResponse> => {
  try {
    const { data } = await api.get<MeResponse>("/auth/me", {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao carregar os dados do usuário.");
    throw error;
  }
};
